import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CallParticipant } from '../../../types/voice-call'
import { useFullscreen } from '../../calls/hooks/use-fullscreen'

/**
 * Tile layout modes for the group / video call grid (Google-Meet style):
 * - `auto`    — everyone equal in a best-fit grid (largest tiles that all fit).
 * - `speaker` — the active speaker (or pinned tile) is featured large; everyone
 *               else rides a filmstrip; self becomes a draggable PiP.
 * - `grid`    — a uniform grid with a capped column count (mobile-friendly).
 */
export type TileViewMode = 'auto' | 'speaker' | 'grid'

export const TILE_VIEW_MODES: TileViewMode[] = ['auto', 'speaker', 'grid']

const STORAGE_KEY = 'ascend-call-tile-view-mode'

function readStoredMode(): TileViewMode {
  if (typeof window === 'undefined') return 'auto'
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && (TILE_VIEW_MODES as string[]).includes(stored)) {
      return stored as TileViewMode
    }
  } catch {
    /* localStorage unavailable (private mode / native) */
  }
  return 'auto'
}

export type TileViewState = {
  mode: TileViewMode
  setMode: (mode: TileViewMode) => void
  /** Pinned participant userId (overrides active-speaker focus when set). */
  pinnedUserId: string | null
  togglePin: (userId: string) => void
  /**
   * True when the layout features a single large tile (speaker mode or any pin).
   * `auto` / `grid` render the equal grid.
   */
  isSpotlight: boolean
  /** Participant userId to feature large in spotlight layouts (null in grid). */
  focusedUserId: string | null
  isFullscreen: boolean
  toggleFullscreen: () => void
  /** Element that should go fullscreen on web (the overlay root). */
  fullscreenRef: React.RefObject<HTMLDivElement | null>
  fullscreenSupported: boolean
}

function isLive(p: CallParticipant): boolean {
  return p.state !== 'left' && p.state !== 'declined'
}

/**
 * Owns tile-view mode (persisted per device), pin selection, active-speaker
 * tracking, and the web Fullscreen API toggle. All state lives in hooks declared
 * unconditionally so this can be called above the overlay's early returns
 * (keeps the iOS hook-count stable — see the overlay's defensive comments).
 */
export function useTileView(
  participants: CallParticipant[],
  currentUserId: string | undefined,
): TileViewState {
  const [mode, setModeState] = useState<TileViewMode>(readStoredMode)
  const [pinnedUserId, setPinnedUserId] = useState<string | null>(null)
  const {
    ref: fullscreenRef,
    isFullscreen,
    supported: fullscreenSupported,
    toggle: toggleFullscreen,
  } = useFullscreen<HTMLDivElement>()
  const lastSpeakerRef = useRef<string | null>(null)

  const setMode = useCallback((next: TileViewMode) => {
    setModeState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore persistence failure */
    }
  }, [])

  const togglePin = useCallback((userId: string) => {
    setPinnedUserId((prev) => (prev === userId ? null : userId))
  }, [])

  // Drop a pin when that participant leaves the call.
  useEffect(() => {
    if (!pinnedUserId) return
    if (!participants.some((p) => p.userId === pinnedUserId && isLive(p))) {
      setPinnedUserId(null)
    }
  }, [participants, pinnedUserId])

  // Remember the most recent non-self speaker so the spotlight doesn't fall back
  // to nobody during silence.
  const speakingUserId = useMemo(() => {
    const speaker = participants.find(
      (p) => p.speaking && p.state === 'active' && p.userId !== currentUserId,
    )
    if (speaker) lastSpeakerRef.current = speaker.userId
    // Keep the remembered speaker only while they're still live.
    if (
      lastSpeakerRef.current &&
      !participants.some((p) => p.userId === lastSpeakerRef.current && isLive(p))
    ) {
      lastSpeakerRef.current = null
    }
    return lastSpeakerRef.current
  }, [participants, currentUserId])

  const isSpotlight = mode === 'speaker' || pinnedUserId != null

  const focusedUserId = useMemo(() => {
    if (!isSpotlight) return null
    if (pinnedUserId) return pinnedUserId
    if (speakingUserId) return speakingUserId
    // No active speaker yet — feature the first live remote, else self.
    const firstRemote = participants.find(
      (p) => p.userId !== currentUserId && p.state === 'active',
    )
    return firstRemote?.userId ?? currentUserId ?? null
  }, [isSpotlight, pinnedUserId, speakingUserId, participants, currentUserId])

  return {
    mode,
    setMode,
    pinnedUserId,
    togglePin,
    isSpotlight,
    focusedUserId,
    isFullscreen,
    toggleFullscreen,
    fullscreenRef,
    fullscreenSupported,
  }
}
